import { useRef, useState } from "react";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "../App";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import BulkDropzone from "./bulk/BulkDropzone";
import BulkStats from "./bulk/BulkStats";
import BulkResultsTable from "./bulk/BulkResultsTable";
import { countByStatus, resultsToCsv, downloadCsv } from "../lib/format";

export default function BulkPage() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [batchId, setBatchId] = useState(null);

  const pickFile = () => inputRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResults([]);
    setBatchId(null);
  };

  const onSubmit = async () => {
    if (!file) {
      toast.error("Pick a CSV file first");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setLoading(true);
    try {
      const res = await api.post("/lookup/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(res.data.results || []);
      setBatchId(res.data.batch_id);
      toast.success(`Processed ${res.data.count} parcels`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const onExport = () => {
    if (!results.length) return;
    downloadCsv(`wetland_lookup_${batchId || "results"}.csv`, resultsToCsv(results));
  };

  const counts = countByStatus(results);

  return (
    <div className="p-6 sm:p-8 md:p-12 max-w-7xl mx-auto w-full">
      <PageHeader
        eyebrow="Batch"
        title="Bulk parcel lookup"
        subtitle="Upload a CSV with one parcel ID per row (first column). We'll look them up and return a table you can export."
      >
        {results.length > 0 && (
          <Button
            onClick={onExport}
            data-testid="bulk-export-btn"
            variant="outline"
            className="border-border/60 hover:bg-[#F4F5F4]"
          >
            <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Export CSV
          </Button>
        )}
      </PageHeader>

      <BulkDropzone file={file} inputRef={inputRef} onFileChange={onFile} onClick={pickFile} />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button
          onClick={onSubmit}
          disabled={!file || loading}
          data-testid="bulk-submit-btn"
          className="bg-[#1A3626] hover:bg-[#2D4A3A] text-white"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-4 w-4 mr-2" strokeWidth={1.5} />
          )}
          Run lookup
        </Button>
        {file && !loading && <span className="text-sm text-[#5B695E]">{file.name}</span>}
      </div>

      {results.length > 0 && (
        <>
          <BulkStats counts={counts} />
          <BulkResultsTable results={results} />
        </>
      )}
    </div>
  );
}
